<template>
    <div :class="rootClass">
        <div :class="contentWrapperClass">
            <div :class="textClass">
                <div v-if="description">
                    <div v-if="isCollapsed" class="relative">
                        <div
                            v-safe-html="previewHtml"
                            class="prose dark:prose-invert"
                        ></div>
                        <div
                            class="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white dark:from-slate-800 to-transparent pointer-events-none"
                        ></div>
                    </div>
                    <div
                        v-else
                        v-safe-html="description"
                        class="prose dark:prose-invert expanded-description"
                    ></div>
                </div>
                <div v-else-if="shortDescription">
                    {{ shortDescription }}
                </div>
                <div v-else class="text-slate-500 dark:text-slate-400 italic">
                    No description available for this program.
                </div>
            </div>

            <div
                v-if="description && isOverPreviewLimit"
                :class="toggleContainerClass"
            >
                <button
                    @click="showFull = !showFull"
                    :class="toggleButtonClass"
                >
                    {{ showFull ? 'Show less' : 'Show more' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
    description: String,
    shortDescription: String,
    variant: {
        type: String,
        default: 'mobile',
        validator: (v) => ['mobile', 'desktop'].includes(v),
    },
});

const showFull = ref(false);

const PREVIEW_CHAR_LIMIT = 300;

/**
 * Flatten the description HTML to plain text for the collapsed preview.
 *
 * Parsing rather than regex-stripping matters: descriptions are authored in a
 * WYSIWYG that emits character entities (`&mdash;`, `&rsquo;`, `&amp;`), and a
 * tag-only strip leaves those codes in the string. `escapeHtml` below then
 * escapes their `&`, so the preview rendered the literal text `&mdash;` while
 * the expanded view — which hands the raw HTML to `v-safe-html` — showed a real
 * em dash (SUPPORT-789). Reading `textContent` decodes entities the same way
 * the browser does for the expanded path.
 */
const plainText = computed(() => {
    const html = props.description || '';
    try {
        const parsed = new DOMParser().parseFromString(html, 'text/html');

        return (parsed.documentElement.textContent || '')
            .replace(/\s+/g, ' ')
            .trim();
    } catch {
        return '';
    }
});

const isOverPreviewLimit = computed(
    () => plainText.value.length > PREVIEW_CHAR_LIMIT
);

const isCollapsed = computed(() => !showFull.value && isOverPreviewLimit.value);

const escapeHtml = (str) =>
    str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

/**
 * Cut the preview back to the last whole word so the ellipsis never lands
 * mid-word, then drop any trailing punctuation the cut left dangling.
 */
const previewText = computed(() => {
    const text = plainText.value;

    if (!isOverPreviewLimit.value) {
        return text;
    }

    const slice = text.slice(0, PREVIEW_CHAR_LIMIT);
    const lastSpace = slice.lastIndexOf(' ');

    return (lastSpace > 0 ? slice.slice(0, lastSpace) : slice).replace(
        /[\s.,;:!?\u2013\u2014-]+$/,
        ''
    );
});

const previewHtml = computed(() => {
    if (!props.description) return '';
    return `<span>${escapeHtml(previewText.value)}...</span>`;
});

const rootClass = computed(() =>
    props.variant === 'desktop'
        ? 'flex flex-col items-start flex-1'
        : 'mt-4 max-w-none'
);

const contentWrapperClass = computed(() =>
    props.variant === 'desktop'
        ? 'flex max-w-[720px] flex-col items-start gap-4 self-stretch'
        : ''
);

const textClass = computed(() =>
    props.variant === 'desktop'
        ? 'self-stretch text-slate-800 dark:text-slate-100 text-base font-normal leading-6'
        : 'text-slate-800 dark:text-slate-100 text-sm font-normal leading-6'
);

const toggleContainerClass = computed(() =>
    props.variant === 'desktop'
        ? 'flex h-9 pt-4 flex-col items-start gap-2.5 self-stretch'
        : 'flex h-9 pt-4'
);

const toggleButtonClass = computed(() =>
    props.variant === 'desktop'
        ? 'text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors duration-200'
        : 'text-slate-800 dark:text-slate-100 hover:text-slate-500 dark:hover:text-slate-300 text-sm font-medium transition-colors duration-200 rounded'
);
</script>
